import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpdeskRecipesAPI } from "../../../../api/helpdeskRecipes";

export default function RecipesList() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  async function loadRecipes() {
    try {
      setError("");
      const data = await HelpdeskRecipesAPI.list();
      setRecipes(data.recipes || []);
    } catch (err) {
      console.error("Failed to load recipes", err);
      setError("Failed to load recipes.");
    } finally {
      setLoading(false);
    }
  }

  async function toggleRecipe(recipe) {
    try {
      setError("");

      if (recipe.enabled) {
        await HelpdeskRecipesAPI.disable(recipe.id);
      } else {
        await HelpdeskRecipesAPI.enable(recipe.id);
      }

      await loadRecipes();
    } catch (err) {
      console.error("Failed to update recipe", err);
      setError("Failed to update recipe.");
    }
  }

  async function deleteRecipe(recipe) {
    if (!window.confirm(`Delete recipe "${recipe.name}"?`)) {
      return;
    }

    try {
      setError("");
      await HelpdeskRecipesAPI.delete(recipe.id);
      await loadRecipes();
    } catch (err) {
      console.error("Failed to delete recipe", err);
      setError("Failed to delete recipe.");
    }
  }

  useEffect(() => {
    loadRecipes();
  }, []);

  if (loading) {
    return <div>Loading recipes…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Recipes</h2>

          <p className="text-sm text-gray-600 mt-1">
            Create reusable sequences of actions that technicians can run
            manually against tickets.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/helpdesk/automation/recipes/new")}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          New recipe
        </button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}

      {recipes.length === 0 ? (
        <div className="border rounded p-6 text-sm text-gray-600">
          No recipes have been created yet.
        </div>
      ) : (
        <div className="border rounded overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Scope</th>
                <th className="p-3 text-left">Audience</th>
                <th className="p-3 text-center">Enabled</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {recipes.map((recipe) => {
                const departmentCount =
                  recipe.department_ids?.length || 0;
                const roleCount = recipe.role_ids?.length || 0;

                let audience = "All technicians";

                if (departmentCount && roleCount) {
                  audience = `${departmentCount} department${
                    departmentCount === 1 ? "" : "s"
                  }, ${roleCount} role${roleCount === 1 ? "" : "s"}`;
                } else if (departmentCount) {
                  audience = `${departmentCount} department${
                    departmentCount === 1 ? "" : "s"
                  }`;
                } else if (roleCount) {
                  audience = `${roleCount} role${
                    roleCount === 1 ? "" : "s"
                  }`;
                }

                return (
                  <tr
                    key={recipe.id}
                    className="border-t hover:bg-gray-50"
                  >
                    <td className="p-3">
                      <button
                        type="button"
                        className="text-blue-600 hover:underline text-left"
                        onClick={() =>
                          navigate(
                            `/helpdesk/automation/recipes/${recipe.id}`
                          )
                        }
                      >
                        {recipe.name}
                      </button>

                      {recipe.description && (
                        <div className="text-xs text-gray-500 mt-1">
                          {recipe.description}
                        </div>
                      )}
                    </td>

                    <td className="p-3 text-sm">
                      {recipe.scope_type === "global"
                        ? "All schools"
                        : "Single school"}
                    </td>

                    <td className="p-3 text-sm">{audience}</td>

                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        checked={Boolean(recipe.enabled)}
                        onChange={() => toggleRecipe(recipe)}
                      />
                    </td>

                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/helpdesk/automation/recipes/${recipe.id}`
                            )
                          }
                          className="text-blue-600"
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteRecipe(recipe)}
                          className="text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}